/**
 * BuriedVerse — SUBSCRIBE / COMMENT call-to-action animations (3 variants).
 *
 * PLACEMENT CONTRACT (1280x704 base). The existing kit owns:
 *   BvFactTag  top-LEFT   scrim  left:0  top:0   700 x 340
 *   BvHero     BOTTOM     scrim  full-width, bottom:0, height 300   (y 404 -> 704)
 *   BvLogo     top-RIGHT  top:20 right:20 w:70                      (y 20 -> ~90)
 * => the only genuinely free band is RIGHT-MIDDLE: x ~720-1280, y ~120-390.
 * Every variant below is right-anchored inside x 834-1234 / y 186-326, so it can fire
 * at the same time as a fact tag AND a hero card without touching either.
 *
 * Skin follows BuriedVerseOverlayDemo.tsx exactly: warm aged-ink palette, EBGaramond
 * serif + Lato sans, SOFT short-range shadow, Easing.out(Easing.cubic), and scrims that
 * are WASHES anchored to the edge which reach alpha 0 INSIDE their own box (never a
 * panel with a hard cut edge).
 *
 * Icons are drawn as plain SVG geometry (bell dome + clapper, speech-bubble outline).
 * No platform trademark asset is used or imitated.
 */
import { AbsoluteFill, useCurrentFrame, interpolate, Easing } from "remotion";
import { loadFont as loadSerif } from "@remotion/google-fonts/EBGaramond";
import { loadFont as loadSans } from "@remotion/google-fonts/Lato";

const { fontFamily: serif } = loadSerif("normal", { weights: ["400", "500", "600"], subsets: ["latin"] });
const { fontFamily: sans } = loadSans("normal", { weights: ["400", "700"], subsets: ["latin"] });

const PARCHMENT = "#f7f0e3";
const PARCHMENT_DIM = "#d3c4a9";
const GOLD = "#d9a441";
const SOFT = "0 1px 3px rgba(20,12,6,0.85), 0 2px 14px rgba(20,12,6,0.45)";

export type CtaKind = "subscribe" | "comment";

// Defaults in the channel's calm "translator" voice — evidence-first, not hype.
// Research-set (2026-07-24): <=12 words on the primary line, one ask only, no stacked
// asks, no imperatives/exclamations. Comment prompts are QUESTION-based (question CTAs
// outperform generic "comment below" in every CTA source reviewed).
const COPY: Record<CtaKind, { kicker: string; line: string }> = {
  subscribe: { kicker: "BuriedVerse", line: "If this kind of digging is useful, subscribing keeps it coming." },
  comment: { kicker: "The comments", line: "What's the word you'd want decoded next? Tell us below." },
};

// ── icons (pure SVG geometry, gold stroke, no trademark asset) ──
const BellIcon: React.FC<{ size?: number; draw?: number }> = ({ size = 26, draw = 1 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ opacity: draw }}>
    <path
      d="M12 3.2c-3.1 0-5.3 2.4-5.3 5.5 0 4.1-1.1 5.6-1.9 6.4-.35.35-.1.95.4.95h13.6c.5 0 .75-.6.4-.95-.8-.8-1.9-2.3-1.9-6.4 0-3.1-2.2-5.5-5.3-5.5z"
      stroke={GOLD}
      strokeWidth={1.5}
      strokeLinejoin="round"
    />
    <path d="M10.1 18.6a2 2 0 0 0 3.8 0" stroke={GOLD} strokeWidth={1.5} strokeLinecap="round" />
  </svg>
);

const BubbleIcon: React.FC<{ size?: number; draw?: number }> = ({ size = 26, draw = 1 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ opacity: draw }}>
    <path
      d="M4 6.2c0-1 .8-1.8 1.8-1.8h12.4c1 0 1.8.8 1.8 1.8v8.2c0 1-.8 1.8-1.8 1.8H10l-4.2 3.4c-.5.4-1.2 0-1.2-.6v-2.8H5.8C4.8 16.2 4 15.4 4 14.4z"
      stroke={GOLD}
      strokeWidth={1.5}
      strokeLinejoin="round"
    />
  </svg>
);

const Icon: React.FC<{ kind: CtaKind; size?: number; draw?: number }> = ({ kind, size, draw }) =>
  kind === "subscribe" ? <BellIcon size={size} draw={draw} /> : <BubbleIcon size={size} draw={draw} />;

type CtaProps = { kind?: CtaKind; kicker?: string; line?: string; startFrame?: number };

/* ═══════════════════════════════════════════════════════════════════════════
   VARIANT A — "PINNED"  (closest sibling of BvFactTag: an edge wash + stacked text)
   Right-edge radial wash, content slides in from +24px and settles. Gold rule
   draws in beneath. The most in-family of the three.
   ═══════════════════════════════════════════════════════════════════════════ */
export const BvCtaPinned: React.FC<CtaProps> = ({ kind = "subscribe", kicker, line, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const c = COPY[kind];
  // 16fps: entrance 8f = 500ms (research: 400-600ms). Scrim leads the text by 2f (~125ms)
  // so the copy arrives onto an already-legible surface instead of popping with it.
  const so = interpolate(frame, [0, 8], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const o = interpolate(frame, [2, 10], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const x = interpolate(frame, [2, 10], [24, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const rule = interpolate(frame, [6, 14], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  if (frame < 0) return null;
  return (
    <>
      {/* edge wash — reaches alpha 0 at 100% of its own extent, inside a LARGER box,
          so no side of the div can show a hard cut (same rule as the fact-tag scrim) */}
      <div
        style={{
          position: "absolute",
          right: 0,
          top: 120,
          width: 620,
          height: 300,
          opacity: so,
          background:
            "radial-gradient(ellipse 540px 260px at 100% 50%, rgba(22,14,7,0.78) 0%, rgba(22,14,7,0.48) 34%, rgba(22,14,7,0.19) 62%, rgba(22,14,7,0.05) 84%, rgba(22,14,7,0) 100%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: 46,
          top: 206,
          width: 400,
          textAlign: "right",
          opacity: o,
          transform: `translateX(${x}px)`,
        }}
      >
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10 }}>
          <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, letterSpacing: 3.4, color: GOLD, textTransform: "uppercase", textShadow: SOFT }}>
            {kicker ?? c.kicker}
          </div>
          <Icon kind={kind} size={24} />
        </div>
        <div style={{ height: 1.5, width: `${rule * 100}%`, marginLeft: "auto", marginTop: 9, background: GOLD, opacity: 0.85 }} />
        <div style={{ fontFamily: serif, fontSize: 27, fontWeight: 500, color: PARCHMENT, marginTop: 11, lineHeight: 1.3, textShadow: SOFT }}>
          {line ?? c.line}
        </div>
      </div>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   VARIANT B — "RULE"  (the restrained one: NO scrim at all)
   A gold hairline draws left->right, text fades and rises under it. Lightest
   possible touch — reads as a margin note rather than an ad.
   ═══════════════════════════════════════════════════════════════════════════ */
export const BvCtaRule: React.FC<CtaProps> = ({ kind = "subscribe", kicker, line, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const c = COPY[kind];
  // rule draws first (8f = 500ms), text fades+rises onto it 2f later — same lead-in
  // principle as the pinned variant's scrim, just with the hairline as the surface.
  const rule = interpolate(frame, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const o = interpolate(frame, [2, 10], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const y = interpolate(frame, [2, 10], [12, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  if (frame < 0) return null;
  return (
    <div style={{ position: "absolute", right: 46, top: 200, width: 400, textAlign: "right" }}>
      <div style={{ height: 1.5, width: `${rule * 100}%`, marginLeft: "auto", background: GOLD, opacity: 0.9 }} />
      <div style={{ opacity: o, transform: `translateY(${y}px)`, marginTop: 14 }}>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10 }}>
          <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, letterSpacing: 3.4, color: GOLD, textTransform: "uppercase", textShadow: SOFT }}>
            {kicker ?? c.kicker}
          </div>
          <Icon kind={kind} size={22} />
        </div>
        <div style={{ fontFamily: serif, fontSize: 29, fontWeight: 500, color: PARCHMENT, marginTop: 8, lineHeight: 1.28, textShadow: SOFT }}>
          {line ?? c.line}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   VARIANT C — "SEAL"  (the diorama-native one)
   A struck wax-seal / stamp disc scales in with a settle, icon drawn inside it,
   copy set to its left. Most distinctive against a storybook plate.
   ═══════════════════════════════════════════════════════════════════════════ */
export const BvCtaSeal: React.FC<CtaProps> = ({ kind = "subscribe", kicker, line, startFrame = 0 }) => {
  const frame = useCurrentFrame() - startFrame;
  const c = COPY[kind];
  const o = interpolate(frame, [0, 7], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  // ONE tiny overshoot then settle — a struck stamp, not a bouncy pop. Research caps the
  // amplitude at 0.94 -> ~1.02 -> 1.0; anything with a second visible oscillation reads gamified.
  const s = interpolate(frame, [0, 6, 12], [0.94, 1.02, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const ring = interpolate(frame, [5, 14], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const tx = interpolate(frame, [4, 12], [14, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.out(Easing.cubic),
  });
  const to = interpolate(frame, [4, 12], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (frame < 0) return null;
  const D = 92;
  return (
    <div
      style={{
        position: "absolute",
        right: 46,
        top: 196,
        width: 470,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 18,
      }}
    >
      <div style={{ textAlign: "right", opacity: to, transform: `translateX(${tx}px)` }}>
        <div style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, letterSpacing: 3.4, color: GOLD, textTransform: "uppercase", textShadow: SOFT }}>
          {kicker ?? c.kicker}
        </div>
        <div style={{ fontFamily: serif, fontSize: 26, fontWeight: 500, color: PARCHMENT, marginTop: 5, lineHeight: 1.3, textShadow: SOFT }}>
          {line ?? c.line}
        </div>
      </div>
      <div style={{ position: "relative", width: D, height: D, opacity: o, transform: `scale(${s})`, flexShrink: 0 }}>
        {/* soft wash under the disc so it never sits on raw brushwork */}
        <div
          style={{
            position: "absolute",
            left: -34,
            top: -34,
            width: D + 68,
            height: D + 68,
            background:
              "radial-gradient(circle at 50% 50%, rgba(22,14,7,0.72) 0%, rgba(22,14,7,0.40) 42%, rgba(22,14,7,0.12) 70%, rgba(22,14,7,0) 100%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: `1.5px solid ${GOLD}`,
            opacity: 0.9,
            background: "radial-gradient(circle at 38% 32%, rgba(217,164,65,0.20) 0%, rgba(217,164,65,0.05) 60%, rgba(217,164,65,0) 100%)",
          }}
        />
        {/* inner hairline ring draws up after the strike */}
        <div
          style={{
            position: "absolute",
            inset: 9,
            borderRadius: "50%",
            border: `1px solid ${PARCHMENT_DIM}`,
            opacity: ring * 0.55,
          }}
        />
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon kind={kind} size={36} />
        </div>
      </div>
    </div>
  );
};

/* ── demo scaffold: CTA fired OVER a live fact tag + hero card, so the placement
      contract above is provable on screen, not just asserted in a comment ── */
export const BvCtaFadeOut: React.FC<{ dur: number; children: React.ReactNode }> = ({ dur, children }) => {
  const frame = useCurrentFrame();
  // Exit is DELIBERATELY faster than the 8f entrance (6f = 375ms, ~0.75x). The asymmetry
  // is the documented convention (Material: 225ms enter / 195ms exit) and reads as natural;
  // a symmetric or slower exit feels like the card is reluctant to leave.
  const o = interpolate(frame, [dur - 6, dur], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.in(Easing.cubic),
  });
  return <AbsoluteFill style={{ opacity: o }}>{children}</AbsoluteFill>;
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
